import { editPostEvent, PreviewService } from "../helpers";
import { JSONContent } from "@tiptap/react";
import { MIME_EXT_DICTINARY } from "@services/s3/constants";
import { createHash } from "crypto";
import { uuid } from "uuidv4";
import { useCreatePost } from "./useCreatePost";
import { useFileS3ArrayUpload } from "@services/s3/hooks/useFileS3ArrayUpload";
import { useUpdatePost } from "./useUpdatePost";
import { ReactQueryOptions, RouterInputs } from "@api";
import { useState } from "react";
import { useShowToast } from "@hooks/useShowToast";
import { headingNodeType, imageNodeType } from "@components/UIKit/TextToolbar/utils/constants";
import { LOCAL_LOADED_IMAGE, MAX_POST_PICTURE_FILE_SIZE, MAX_POST_PICTURES } from "../constants";
const hashImageTitle = (title: string) => {
  const type = title.split('.').pop() as string;
  const contentType = type ? `image/${type}` : 'application/octet-stream';
  return createHash('sha256').update(title).digest('hex') + MIME_EXT_DICTINARY[contentType];
};
type optionsCreateType = ReactQueryOptions['PostsManager']['create'];
type inputCreateType = RouterInputs['PostsManager']['create'];
type optionsUpdateType = ReactQueryOptions['PostsManager']['update'];
type inputUpdateType = RouterInputs['PostsManager']['update'];
const validatePost = (content: JSONContent[]) => {
  const hasImage = content.some(el => el.type === imageNodeType);
  const hasTitle = content.some(el => el.type === headingNodeType && el?.content && el.content.length > 0);
  const images = content.filter(el => el.type === imageNodeType);
  const allImagesValid = content.every(el => {
    if (el.type === imageNodeType && el.attrs?.src) {
      if (el.attrs.src.startsWith(LOCAL_LOADED_IMAGE)) {
        const base64String = el.attrs.src.split(',')[1];
        const imageSizeInBytes = base64String.length * 3 / 4 - (base64String.endsWith('==') ? 2 : base64String.endsWith('=') ? 1 : 0);
        return imageSizeInBytes <= MAX_POST_PICTURE_FILE_SIZE;
      }
    }
    return true;
  });
  const tooManyImages = images.length > MAX_POST_PICTURES;
  const message = !hasTitle ? 'Post title is empty' : !hasImage ? 'Post image is empty' : tooManyImages ? 'Too many images' : !allImagesValid ? 'Images are not valid' : null;
  return {
    message,
    isValid: hasTitle && hasImage && allImagesValid && !tooManyImages
  };
};
export const useUploadPost = () => {
  const {
    showToast
  } = useShowToast();
  const {
    mutateAsync: createPost
  } = useCreatePost();
  const {
    mutateAsync: updatePost
  } = useUpdatePost();
  const {
    handleUpload
  } = useFileS3ArrayUpload();
  const [error, setError] = useState<string | null>(null);
  const uploadPost = async (input: inputCreateType, options?: optionsCreateType) => {
    editPostEvent('loading');
    setError(null);
    const {
      content_new: contentData,
      ...rest
    } = input;
    const content = contentData as JSONContent[];
    if (!content) {
      editPostEvent('error');
      setError('Post content is empty');
      showToast({
        variant: 'error',
        text: 'content is empty'
      });
      return;
    }
    const {
      isValid,
      message
    } = validatePost(content);
    if (!isValid) {
      editPostEvent('error');
      setError(message);
      showToast({
        variant: 'error',
        text: message || 'not valid'
      });
      return;
    }
    const imagesToSave = content.filter(c => c.type === imageNodeType && c?.attrs?.src?.startsWith(LOCAL_LOADED_IMAGE)).map(c => c.attrs);
    const dtoImagesToSave = imagesToSave?.map(i => {
      const hash = hashImageTitle(i?.title);
      return {
        src: hash,
        data: i?.src,
        title: hash
      };
    });
    if (!dtoImagesToSave) return; // для TS, isValid проверяет на наличие картинок

    await handleUpload(dtoImagesToSave);
    const contentToSave = content.map(el => {
      if (el.type === 'image' && el.attrs?.src.startsWith(LOCAL_LOADED_IMAGE)) {
        return {
          ...el,
          attrs: {
            ...el.attrs,
            src: dtoImagesToSave.find(f => f.title === hashImageTitle(el?.attrs?.title))?.src
          }
        };
      }
      return el;
    });
    const {
      getHeadingText
    } = PreviewService;
    await createPost({
      ...rest,
      title: getHeadingText(contentToSave),
      content_new: contentToSave
    }, options);
    editPostEvent('disabled');
    setError(null);
  };
  const updateUploadPost = async (input: inputUpdateType, options?: optionsUpdateType) => {
    editPostEvent('loading');
    setError(null);
    const {
      content_new: contentData,
      ...rest
    } = input;
    const content = contentData as JSONContent[];
    if (!content) {
      editPostEvent('error');
      showToast({
        variant: 'error',
        text: 'content is empty'
      });
      return;
    }
    const {
      isValid,
      message
    } = validatePost(content);
    if (!isValid) {
      editPostEvent('error');
      setError(message);
      showToast({
        variant: 'error',
        text: message || 'not valid'
      });
      return;
    }
    const postImages = content.filter(c => c.type === imageNodeType);
    const newImagesToSave = postImages.filter(c => c?.attrs?.src?.startsWith(LOCAL_LOADED_IMAGE)).map(c => c.attrs);
    const dtoImagesToSave = newImagesToSave?.map(i => {
      const hash = hashImageTitle(i?.title);
      return {
        src: hash,
        data: i?.src,
        title: hash
      };
    });
    if (dtoImagesToSave && dtoImagesToSave.length) {
      await handleUpload(dtoImagesToSave);
    }
    const contentToSave = content.map(el => {
      if (el.type === imageNodeType && el.attrs?.src.startsWith(LOCAL_LOADED_IMAGE)) {
        return {
          ...el,
          attrs: {
            ...el.attrs,
            src: dtoImagesToSave?.find(f => f.title === hashImageTitle(el?.attrs?.title))?.src,
            id: uuid()
          }
        };
      }
      return el;
    });
    const {
      getHeadingText
    } = PreviewService;

    // TODO:  найти картинки которые были удалены и удалить из S3
    await updatePost({
      content_new: contentToSave,
      title: getHeadingText(contentToSave),
      ...rest
    }, options);
    editPostEvent('disabled');
  };
  return {
    uploadPost,
    updateUploadPost,
    error
  };
};